import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { gsap, useGSAP } from './lib/gsap';

// Dashed gradient ring, drawn with an SVG stroke (crisp dash edges — no seams between dots).
// --draw (0 → 360) reveals the ring dash by dash; at 360 it is the complete static ring.
// Hero rings are drawn by the intro timeline in App.jsx; others pass drawOnScroll.
const BgDottedCircle = ({ className = '', degrees = 135, drawOnScroll = false, delay = 0, start = 'top 90%' }) => {
  const ref = useRef(null);
  const uid = useId().replace(/[:«»]/g, '');
  // stroke width in viewBox units that renders as 2 CSS px at the ring's actual size
  // (non-scaling-stroke would break pathLength-based dash spacing)
  const [stroke, setStroke] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    const update = () => el.offsetWidth && setStroke((2 * 100) / el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useGSAP(
    () => {
      if (!drawOnScroll) return;
      gsap.fromTo(
        ref.current,
        { '--draw': 0 },
        {
          '--draw': 360,
          // the hero intro hides every ring until it runs — don't depend on it here
          onStart: () => gsap.set(ref.current, { opacity: 1 }),
          delay,
          duration: 1.8,
          ease: 'power2.inOut',
          scrollTrigger: { trigger: ref.current, start, once: true },
        },
      );
    },
    { dependencies: [drawOnScroll, delay, start] },
  );

  // CSS linear-gradient(<degrees>deg, #2D5CFF 0%, transparent 90%) mapped onto the SVG box
  const rad = (degrees * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const half = (Math.abs(dx) + Math.abs(dy)) / 2;
  const g = { x1: 0.5 - dx * half, y1: 0.5 - dy * half, x2: 0.5 + dx * half, y2: 0.5 + dy * half };
  // the reveal starts at 6 o'clock and runs clockwise (6 → 7 → … → 5 → 6);
  // SVG strokes start at 3 o'clock and already run clockwise, so a 90° turn is enough
  const sweepStart = 90;

  return (
    <div ref={ref} className={`bg-dotted-circle absolute -z-10 rounded-full ${className}`} style={{ '--draw': 0 }}>
      <svg viewBox="0 0 100 100" className="h-full w-full overflow-visible" aria-hidden>
        <defs>
          <linearGradient id={`dg-${uid}`} x1={g.x1} y1={g.y1} x2={g.x2} y2={g.y2}>
            <stop offset="0" stopColor="#2D5CFF" />
            <stop offset="0.9" stopColor="#2D3190" stopOpacity="0" />
          </linearGradient>
          <mask id={`dm-${uid}`} maskUnits="userSpaceOnUse" x="-5" y="-5" width="110" height="110">
            <circle
              cx="50"
              cy="50"
              r="49"
              fill="none"
              stroke="white"
              strokeWidth="6"
              pathLength="360"
              strokeDasharray="360 360"
              transform={`rotate(${sweepStart} 50 50)`}
              style={{ strokeDashoffset: 'calc(360 - var(--draw))' }}
            />
          </mask>
        </defs>
        {/* 18 dashes, gap/dash every 10°. Not rotated — a transform would rotate the gradient too.
            Strokes start at 3 o'clock; at 12 o'clock (270°) the 20° pattern sits at 10° → a gap
            begins there, exactly like the original CSS ring. */}
        <circle
          cx="50"
          cy="50"
          r="49"
          fill="none"
          stroke={`url(#dg-${uid})`}
          strokeWidth={stroke}
          pathLength="360"
          strokeDasharray="10 10"
          mask={`url(#dm-${uid})`}
        />
      </svg>
    </div>
  );
};

export default BgDottedCircle;
